import { createRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Route as AdminLayoutRoute } from "./_layout";
import { Badge, BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { StatCard } from "@/components/application/metrics/stat-card";
import { SpzBudgetTable } from "../../components/custom/SpzBudgetTable";
import { EurAdvisoryBanner } from "../../components/custom/EurAdvisoryBanner";
import { UnassignedSpzSlideOver } from "../../components/custom/UnassignedSpzSlideOver";
import { BudgetApproveDialog } from "../../components/custom/BudgetApproveDialog";
import {
  useBudgetDetail,
  useBudgetCardCandidates,
  useBudgetUnassignedCards,
} from "../../hooks/useBudgetQueries";
import {
  useApproveBudget,
  useAssignCardHolder,
} from "../../hooks/useBudgetMutations";
import { useHoldersList } from "../../hooks/useHolderQueries";
import {
  formatBudgetMonth,
  formatEur,
  formatKc,
} from "../../schemas/budget";

/**
 * UC-002 Budget detail — per-SPZ Data Table + drill-down + approve confirm
 * (Phase 16, REQ-BIZ-007..009, princip #2 + #3 + #4).
 *
 * Layout:
 *   - Header: měsíc + Středisko + status Badge + back link.
 *   - Stat cards row: Celkem CZK, Celkem EUR, SPZ, Transakce.
 *   - Sync-incomplete warning Alert (princip #4).
 *   - EUR advisory banner (princip #2).
 *   - SpzBudgetTable s expandable row drill-down.
 *   - Actions: "Vyřešit nepřiřazené SPZ" + "Schválit" (3-stupňový dialog).
 *   - Po approved_at → read-only mode (žádné action buttons).
 */

export const Route = createRoute({
  getParentRoute: () => AdminLayoutRoute,
  path: "/admin/budgets/$monthId",
  component: AdminBudgetDetailPage,
});

function AdminBudgetDetailPage() {
  const { monthId } = Route.useParams();
  const detail = useBudgetDetail(monthId);
  const unassigned = useBudgetUnassignedCards(monthId, {
    enabled: detail.data?.status === "draft",
  });
  const [slideOverOpen, setSlideOverOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const candidates = useBudgetCardCandidates(monthId, {
    enabled: slideOverOpen,
  });
  const holders = useHoldersList(undefined, { enabled: slideOverOpen });
  const approveMutation = useApproveBudget();
  const assignMutation = useAssignCardHolder();

  const isApproved = detail.data?.status === "approved";
  const isReturned = detail.data?.status === "returned";
  const readOnly = isApproved;
  // Transakce na neznámou kartu blokují schválení (EC3).
  const unassignedCount = unassigned.data?.groups.length ?? 0;

  const headerSubtitle = useMemo(() => {
    if (!detail.data) return "";
    return `${detail.data.billing_group_code} (${detail.data.zone_code}) · ${formatBudgetMonth(detail.data.month)}`;
  }, [detail.data]);

  if (detail.isLoading) {
    return (
      <section className="max-w-7xl mx-auto" aria-busy="true">
        <h2 className="text-2xl font-bold text-primary m-0">Detail rozpočtu</h2>
        <p className="text-tertiary">Načítám detail rozpočtu…</p>
      </section>
    );
  }

  if (detail.error || !detail.data) {
    return (
      <section className="max-w-7xl mx-auto space-y-4">
        <h2 className="text-2xl font-bold text-primary m-0">Detail rozpočtu</h2>
        <InlineAlert variant="error" title="Detail rozpočtu se nepodařilo načíst">
          {detail.error instanceof Error
            ? detail.error.message
            : "Neznámá chyba."}
        </InlineAlert>
        <Link to="/admin/budgets" className="text-sm text-brand-secondary hover:underline">
          ← Zpět na seznam
        </Link>
      </section>
    );
  }

  const data = detail.data;

  return (
    <section
      aria-labelledby="budget-detail-title"
      data-testid="budget-detail"
      data-status={data.status}
      className="max-w-7xl mx-auto space-y-6"
    >
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex flex-col gap-1">
          <Link
            to="/admin/budgets"
            className="text-sm text-brand-secondary hover:underline mb-1"
          >
            ← Zpět na seznam
          </Link>
          <h2 id="budget-detail-title" className="text-2xl font-bold text-primary m-0">
            Detail rozpočtu — {headerSubtitle}
          </h2>
          <div className="flex gap-1.5 flex-wrap items-center mt-1">
            <span data-testid="budget-detail-status">
              <BadgeWithDot
                color={
                  isApproved ? "success" : isReturned ? "warning" : "blue"
                }
                type="pill-color"
              >
                {isApproved
                  ? "Schválen"
                  : isReturned
                    ? "Vrácen k opravě"
                    : "Draft"}
              </BadgeWithDot>
            </span>
            {data.eur_advisory_flag && <Badge color="blue">EUR</Badge>}
            {!data.sync_complete && (
              <Badge color="warning">Neúplný sync</Badge>
            )}
            {data.approved_at && (
              <span className="text-xs text-tertiary ml-1">
                · schváleno{" "}
                <time dateTime={data.approved_at}>
                  {formatDate(data.approved_at)}
                </time>
              </span>
            )}
          </div>
        </div>
        <div
          className="flex gap-2"
          data-testid="budget-detail-actions"
        >
          {!readOnly && (
            <>
              <Button
                color="secondary"
                onPress={() => setSlideOverOpen(true)}
                data-testid="budget-resolve-unassigned"
              >
                {unassignedCount > 0
                  ? `Přiřadit držitele · ${unassignedCount} neznámých karet`
                  : "Přiřadit držitele"}
              </Button>
              <Button
                color="primary"
                onPress={() => setApproveOpen(true)}
                isDisabled={unassignedCount > 0}
                data-testid="budget-approve-open"
              >
                Schválit
              </Button>
            </>
          )}
        </div>
      </header>

      {!data.sync_complete && (
        <InlineAlert
          variant="warning"
          title="Sync nekompletní"
          data-testid="budget-sync-warning"
        >
          Synchronizace pro daný měsíc nebyla úplná — částky odrážejí jen
          dostupná data. Před schválením spusťte sync.
        </InlineAlert>
      )}

      <EurAdvisoryBanner
        visible={data.eur_advisory_flag}
        billingGroupCode={data.billing_group_code}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Celkem CZK" value={formatKc(data.total_amount_kc)} />
        <StatCard
          label="Celkem EUR"
          value={
            data.eur_advisory_flag ? formatEur(data.total_amount_eur) : "—"
          }
        />
        <StatCard label="Položek (karet)" value={data.items.length} />
        <StatCard
          label="Transakce"
          value={data.items.reduce((sum, item) => sum + item.transaction_count, 0)}
        />
      </div>

      <SpzBudgetTable items={data.items} readOnly={readOnly} />

      <UnassignedSpzSlideOver
        open={slideOverOpen}
        budgetId={monthId}
        candidates={candidates.data?.candidates ?? []}
        unknownGroups={unassigned.data?.groups ?? []}
        holders={
          holders.data?.rows
            .filter((h) => h.deactivated_at === null)
            .map((h) => ({ holder_id: h.id, display_name: h.display_name })) ??
          []
        }
        loading={unassigned.isLoading || candidates.isLoading || holders.isLoading}
        submitting={assignMutation.isPending}
        submitError={
          assignMutation.error instanceof Error
            ? assignMutation.error.message
            : null
        }
        onClose={() => setSlideOverOpen(false)}
        onSubmit={(payload) =>
          assignMutation.mutate(
            { budgetId: monthId, payload },
            {
              onSuccess: () => setSlideOverOpen(false),
            },
          )
        }
      />

      <BudgetApproveDialog
        open={approveOpen}
        month={data.month}
        billingGroupCode={data.billing_group_code}
        totalAmountKc={data.total_amount_kc}
        totalAmountEur={data.total_amount_eur}
        eurAdvisoryFlag={data.eur_advisory_flag}
        itemCount={data.items.length}
        unassignedCount={unassignedCount}
        submitting={approveMutation.isPending}
        submitError={
          approveMutation.error instanceof Error
            ? approveMutation.error.message
            : null
        }
        onCancel={() => setApproveOpen(false)}
        onConfirm={() =>
          approveMutation.mutate(monthId, {
            onSuccess: () => setApproveOpen(false),
          })
        }
      />
    </section>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("cs-CZ", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
